import React from 'react';
import '../../assets/css/case-property.css';
import { useProperty, useContentToken, EditableToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath, formatPhone, phoneDigits, cleanLinkUrl } from './propertyUtils';
import caseBackgroundIcon from '../../assets/images/case-background.svg';
import { FacebookIcon } from '@vibesites/assets/icons/social/FacebookIcon';
import { InstagramIcon } from '@vibesites/assets/icons/social/InstagramIcon';
import { TikTokIcon } from '@vibesites/assets/icons/social/TikTokIcon';

/**
 * Property-specific footer with property logo, address, nav, socials.
 * Matches the Stratman footer bg-black pattern.
 */
export function PropertyPageFooter() {
  const property = useProperty();

  const basePath = getPropertyBasePath(property);
  const name = property.property_name || '';
  const logo = (useContentToken('footer.property_logo', {
    defaultField: 'property_primary_logo',
  }) || property.property_primary_logo || property.property_mono_white_logo || property.property_secondary_logo || '') as string;
  const logoAlt = (useContentToken('footer.property_logo_alt', {
    defaultField: 'property_name',
    defaultValue: 'Property logo',
  }) || name) as string;
  const address = property.property_address_1 || property.property_address || '';
  const city = property.property_address_city || '';
  const state = property.property_address_state || '';
  const zip = property.property_address_zip || '';
  const phone = property.property_call_tracking_phone || property.property_phone || '';
  const applyUrl = cleanLinkUrl(property.property_apply_now_url);
  const residentUrl = cleanLinkUrl(property.property_resident_portal_url);

  const facebookUrl = property.property_facebook_url || '';
  const instagramUrl = property.property_instagram_url || '';
  const tiktokUrl = property.property_tiktok_url || '';

  const selfGuidedTourUrl = (property as any).property_self_guided_tour_url || '';

  const NAV_LINKS: Array<{ href: string; label: string; external: boolean; event?: string }> = [
    { href: `${basePath}/floor-plans`, label: 'Floor Plans', external: false },
    { href: `${basePath}/neighborhood`, label: 'Neighborhood', external: false },
    { href: `${basePath}/pet-policy`, label: 'Pet Friendly Policy', external: false },
    ...(applyUrl ? [{ href: applyUrl, label: 'Apply Online', external: true, event: 'apply_now_clicked' }] : []),
    { href: `${basePath}/amenities`, label: 'Amenities', external: false },
    { href: `${basePath}/photos`, label: 'Gallery', external: false },
    ...(selfGuidedTourUrl ? [{ href: selfGuidedTourUrl, label: 'Self-Guided Tour', external: true, event: 'self_guided_tour_clicked' }] : []),
    { href: `${basePath}/contact-us`, label: 'Contact Us', external: false },
    ...(residentUrl ? [{ href: residentUrl, label: 'Login', external: true, event: 'resident_portal_clicked' }] : []),
  ];

  const SOCIALS = [
    { href: facebookUrl, label: `${name} Facebook link`, icon: <FacebookIcon size={24} color="currentColor" aria-label="Facebook" /> },
    { href: instagramUrl, label: `${name} Instagram link`, icon: <InstagramIcon size={24} color="currentColor" aria-label="Instagram" /> },
    { href: tiktokUrl, label: `${name} Tiktok link`, icon: <TikTokIcon size={24} color="currentColor" aria-label="TikTok" /> },
  ].filter(s => s.href);

  return (
    <footer className="prop-footer">
      <div
        className="prop-footer-overlay"
        role="presentation"
        aria-hidden="true"
      >
        <img src={caseBackgroundIcon} alt="Case & Associates logo accent background" />
      </div>
      <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 1.5rem' }}>
        <div className="prop-footer-top">
          {logo && (
            <a href={`${basePath}/`} className="prop-footer-logo">
              <EditableToken path="footer.property_logo" type="image" label="Property Logo">
                <VibesitesImage
                  url={logo}
                  alt={logoAlt}
                  width={280}
                  height={74}
                  style={{ width: 280, height: 74, objectFit: 'contain' }}
                />
              </EditableToken>
            </a>
          )}
          <address>
            {name}<br />
            {address && <>{address}<br /></>}
            {city}{state ? `, ${state}` : ''} {zip}
          </address>
          {phone && (
            <div style={{ textAlign: 'center' }}>
              <div className="prop-footer-help">Have a question or need help?</div>
              <a className="prop-footer-phone" href={`tel:${phoneDigits(phone)}`}>
                Call {formatPhone(phone)}
              </a>
            </div>
          )}
        </div>

        <ul className="prop-footer-nav">
          {NAV_LINKS.map(l => (
            <li key={l.href}>
              <a
                href={l.href}
                data-vibesite-event={l.event}
                {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {SOCIALS.length > 0 && (
          <div className="prop-footer-socials">
            {SOCIALS.map(s => (
              <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer">
                <span className="sr-only">{s.label}</span>
                {s.icon}
              </a>
            ))}
          </div>
        )}

        <ul className="prop-footer-policy">
          <li><a href="/privacy-policy">Privacy Policy</a></li>
          <li><a href="/accessibility-statement">Accessibility Statement</a></li>
        </ul>
      </div>
    </footer>
  );
}

export default PropertyPageFooter;
